import type { ComponentType, KeyboardEvent } from 'react';
import {
  Bold,
  BookOpen,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  Quote,
  Sigma,
  Strikethrough,
  Subscript,
  Superscript,
  Table as TableIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { EditorSelection } from '@/lib/djotEditing';
import {
  codeBlockTemplate,
  insertBlockAtCursor,
  insertFootnote,
  insertLink,
  insertWikilink,
  setHeadingLevel,
  tableTemplate,
  thematicBreakTemplate,
  toggleInlineWrap,
  toggleLinePrefix,
  toggleOrderedList,
} from '@/lib/djotEditing';

interface DjotToolbarProps {
  applyEdit: (transform: (sel: EditorSelection) => EditorSelection) => void;
}

interface ToolbarAction {
  label: string;
  icon: ComponentType<{ className?: string }>;
  run: (sel: EditorSelection) => EditorSelection;
  /** Keyboard shortcut hint shown in the tooltip (Ctrl/Cmd handled by caller). */
  shortcut?: string;
}

const textActions: ToolbarAction[] = [
  // Djot uses `*strong*` and `_emphasis_` — the reverse of Markdown.
  { label: 'Bold', icon: Bold, run: (s) => toggleInlineWrap(s, '*', '*', 'strong text'), shortcut: 'Ctrl+B' },
  { label: 'Italic', icon: Italic, run: (s) => toggleInlineWrap(s, '_', '_', 'italic text'), shortcut: 'Ctrl+I' },
  { label: 'Strikethrough', icon: Strikethrough, run: (s) => toggleInlineWrap(s, '{-', '-}', 'deleted text') },
  { label: 'Highlight', icon: Highlighter, run: (s) => toggleInlineWrap(s, '{=', '=}', 'highlighted text') },
  { label: 'Superscript', icon: Superscript, run: (s) => toggleInlineWrap(s, '^', '^', 'sup') },
  { label: 'Subscript', icon: Subscript, run: (s) => toggleInlineWrap(s, '~', '~', 'sub') },
  { label: 'Inline code', icon: Code, run: (s) => toggleInlineWrap(s, '`', '`', 'code'), shortcut: 'Ctrl+E' },
];

const headingActions: ToolbarAction[] = [
  { label: 'Heading 1', icon: Heading1, run: (s) => setHeadingLevel(s, 1) },
  { label: 'Heading 2', icon: Heading2, run: (s) => setHeadingLevel(s, 2) },
  { label: 'Heading 3', icon: Heading3, run: (s) => setHeadingLevel(s, 3) },
];

const listActions: ToolbarAction[] = [
  { label: 'Bulleted list', icon: List, run: (s) => toggleLinePrefix(s, '- ') },
  { label: 'Numbered list', icon: ListOrdered, run: toggleOrderedList },
  { label: 'Task list', icon: ListTodo, run: (s) => toggleLinePrefix(s, '- [ ] ') },
  { label: 'Blockquote', icon: Quote, run: (s) => toggleLinePrefix(s, '> ') },
];

const insertActions: ToolbarAction[] = [
  { label: 'Link', icon: LinkIcon, run: (s) => insertLink(s, false), shortcut: 'Ctrl+K' },
  { label: 'Image', icon: ImageIcon, run: (s) => insertLink(s, true) },
  { label: 'Wikilink [[…]]', icon: BookOpen, run: insertWikilink, shortcut: 'Ctrl+[' },
  { label: 'Code block', icon: Code, run: (s) => insertBlockAtCursor(s, codeBlockTemplate) },
  { label: 'Table', icon: TableIcon, run: (s) => insertBlockAtCursor(s, tableTemplate) },
  { label: 'Math', icon: Sigma, run: (s) => toggleInlineWrap(s, '$`', '`', 'e=mc^2') },
  { label: 'Footnote', icon: Sigma, run: insertFootnote },
  { label: 'Horizontal rule', icon: Minus, run: (s) => insertBlockAtCursor(s, thematicBreakTemplate) },
];

function ToolbarButton({ action, applyEdit }: { action: ToolbarAction; applyEdit: DjotToolbarProps['applyEdit'] }) {
  const Icon = action.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => applyEdit(action.run)}
          aria-label={action.label}
        >
          <Icon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {action.label}
        {action.shortcut ? ` (${action.shortcut})` : ''}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A compact formatting toolbar for the Djot markup editor. Purely
 * dispatches to `applyEdit` with pure text transforms.
 *
 * Unstyled container — the caller (`DjotEditor`) owns the surrounding
 * border/background/rounding, since the toolbar shares that bar with
 * the view-mode toggle.
 */
export function DjotToolbar({ applyEdit }: DjotToolbarProps) {
  return (
    // A single non-wrapping row is required — flex-wrap would make the
    // whole bar grow to multiple lines instead of scrolling (and
    // `whitespace-nowrap` on the wrapper ensures buttons don't reflow).
    <div className="flex flex-nowrap items-center gap-0.5">
      {headingActions.map((action) => (
        <ToolbarButton key={action.label} action={action} applyEdit={applyEdit} />
      ))}
      <Separator orientation="vertical" className="mx-1 h-5" />
      {textActions.map((action) => (
        <ToolbarButton key={action.label} action={action} applyEdit={applyEdit} />
      ))}
      <Separator orientation="vertical" className="mx-1 h-5" />
      {listActions.map((action) => (
        <ToolbarButton key={action.label} action={action} applyEdit={applyEdit} />
      ))}
      <Separator orientation="vertical" className="mx-1 h-5" />
      {insertActions.map((action) => (
        <ToolbarButton key={action.label} action={action} applyEdit={applyEdit} />
      ))}
    </div>
  );
}

/** Groups text-format hotkeys for an editor surface, plus (optionally)
    focus and save shortcuts on the same hot-key surface. `onToggleFocus`
    and `onSave` are provided by the page so the toolbar stays decoupled
    from whichever editor owns them. */
export function handleDjotEditorKeyDown(
  e: KeyboardEvent<HTMLTextAreaElement>,
  applyEdit: DjotToolbarProps['applyEdit'],
  options?: { onToggleFocus?: () => void; onSave?: () => void },
): void {
  const mod = e.metaKey || e.ctrlKey;
  if (!mod) return;

  // Special keys before the formatting ones — these aren't raw-text changes.
  if (options?.onSave && e.key.toLowerCase() === 's' && !e.shiftKey) {
    e.preventDefault();
    options.onSave();
    return;
  }
  if (options?.onToggleFocus && e.key.toLowerCase() === 'f' && e.shiftKey) {
    e.preventDefault();
    options.onToggleFocus();
    return;
  }

  switch (e.key.toLowerCase()) {
    case 'b':
      e.preventDefault();
      applyEdit((s) => toggleInlineWrap(s, '*', '*', 'strong text'));
      break;
    case 'i':
      e.preventDefault();
      applyEdit((s) => toggleInlineWrap(s, '_', '_', 'italic text'));
      break;
    case 'e':
      e.preventDefault();
      applyEdit((s) => toggleInlineWrap(s, '`', '`', 'code'));
      break;
    case 'k':
      e.preventDefault();
      applyEdit((s) => insertLink(s, false));
      break;
    case '[':
      e.preventDefault();
      applyEdit(insertWikilink);
      break;
    default:
      break;
  }
}
